type BuzzTopicLike = {
	_id?: string;
	headline?: string;
	title?: string;
	keyword?: string;
	summary?: string;
	rationale?: string;
	content?: string;
	source?: string;
	publishedAt?: string;
	score?: number | string;
	metadata?: {
		description?: string;
		summary?: string;
		additionalData?: Record<string, unknown>;
	};
};

const truncate = (value: string, maxLength = 360) => {
	const cleaned = value.replace(/\s+/g, ' ').trim();
	if (cleaned.length <= maxLength) return cleaned;
	return `${cleaned.slice(0, maxLength - 1).trim()}...`;
};

const pickText = (values: unknown[]) => {
	for (const value of values) {
		if (typeof value !== 'string') continue;
		const cleaned = value.replace(/\s+/g, ' ').trim();
		if (cleaned) return cleaned;
	}
	return '';
};

const formatClockTime = (date: Date) =>
	new Intl.DateTimeFormat(undefined, {
		hour: 'numeric',
		minute: '2-digit',
	}).format(date);

const isSameLocalDate = (a: Date, b: Date) =>
	a.getFullYear() === b.getFullYear() &&
	a.getMonth() === b.getMonth() &&
	a.getDate() === b.getDate();

const pluralize = (count: number, unit: string) =>
	`${count} ${unit}${count === 1 ? '' : 's'} ago`;

export const formatBuzzTopicPublishedAt = (
	value: string | undefined,
	now = new Date(),
) => {
	if (!value) return '';
	const publishedAt = new Date(value);
	if (Number.isNaN(publishedAt.getTime())) return '';

	const diffMs = Math.max(0, now.getTime() - publishedAt.getTime());
	const diffMinutes = Math.floor(diffMs / 60_000);
	if (diffMinutes < 1) return 'Just now';
	if (diffMinutes < 60) return pluralize(diffMinutes, 'minute');

	const diffHours = Math.floor(diffMinutes / 60);
	if (diffHours < 12) return pluralize(diffHours, 'hour');

	if (isSameLocalDate(publishedAt, now)) {
		return `Today at ${formatClockTime(publishedAt)}`;
	}

	const yesterday = new Date(now);
	yesterday.setDate(now.getDate() - 1);
	if (isSameLocalDate(publishedAt, yesterday)) {
		return `Yesterday at ${formatClockTime(publishedAt)}`;
	}

	return new Intl.DateTimeFormat(undefined, {
		month: 'short',
		day: 'numeric',
		year:
			publishedAt.getFullYear() === now.getFullYear() ? undefined : 'numeric',
		hour: 'numeric',
		minute: '2-digit',
	}).format(publishedAt);
};

export const getBuzzTopicPreviewItem = (
	items: BuzzTopicLike[] | undefined,
	selectedTopicId: string,
) => {
	if (!items?.length) return null;
	if (selectedTopicId && selectedTopicId !== 'auto') {
		return (
			items.find((item) => String(item._id) === String(selectedTopicId)) || null
		);
	}
	return items[0];
};

export const getBuzzTopicTitle = (item: BuzzTopicLike | null) =>
	pickText([item?.headline, item?.title, item?.keyword]) || 'Buzz topic';

export const getBuzzTopicContext = (item: BuzzTopicLike | null) => {
	const additionalData = item?.metadata?.additionalData || {};
	const context = pickText([
		item?.summary,
		item?.rationale,
		item?.content,
		item?.metadata?.summary,
		item?.metadata?.description,
		additionalData.summary,
		additionalData.context,
		additionalData.description,
		additionalData.text,
		additionalData.markdown,
	]);

	if (context) return truncate(context);
	if (item?.source) return `Source context: ${item.source}`;
	return 'No additional context is available for this topic yet.';
};

export const getBuzzTopicMeta = (
	item: BuzzTopicLike | null,
	now = new Date(),
) => {
	const parts = [
		item?.source,
		formatBuzzTopicPublishedAt(item?.publishedAt, now),
	].filter(
		(value): value is string =>
			typeof value === 'string' && value.trim() !== '',
	);
	return parts.join(' · ');
};
